import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { Badge, Button, ConfirmDialog, Input, Select, Toast } from '@/components/ui';

/**
 * Writing a Composer proposal to a bot, or to a bot engine.
 *
 * Proposing and applying are deliberately separate acts. Everything above
 * this panel only suggests; this is the one place that changes anything, and
 * it asks twice — once to show what would change, once to do it.
 *
 * WHY AN ENGINE MATTERS MORE THAN A BOT
 * A fault in a script is almost never in one bot. It is in the engine all of
 * them were built from, so fixing the bot fixes one twentieth of the problem
 * and leaves the rest to be found one complaint at a time. Applying to the
 * engine fixes it at the source — and changes every bot on it at once, which
 * is why the count is shown before the button and checked again on the way
 * in.
 */

interface Change {
  field: string;
  from: unknown;
  to: unknown;
}

interface Preview {
  target: string;
  name: string;
  client_name: string | null;
  affected_bots: number;
  changes: Change[];
  dropped: string[];
  warning: string | null;
}

interface Applied {
  applied: boolean;
  name: string;
  affected_bots: number;
  keys: string[];
  audit_id: number | null;
}

function value(raw: unknown): string {
  if (raw === undefined || raw === null || raw === '') return '—';
  if (typeof raw === 'string') return raw;
  return JSON.stringify(raw);
}

export default function ComposerApply({
  config,
  bots,
  engines,
  defaultTarget,
  defaultTargetId,
}: {
  config: Record<string, unknown>;
  bots: Array<{ id: string; name: string; client_name: string }>;
  engines: Array<{ id: string; name: string; bots: number }>;
  defaultTarget?: 'bot' | 'engine';
  defaultTargetId?: string;
}) {
  const qc = useQueryClient();
  const [target, setTarget] = useState<'bot' | 'engine'>(defaultTarget ?? 'bot');
  const [targetId, setTargetId] = useState(defaultTargetId ?? '');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [undo, setUndo] = useState<Applied | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  const body = () => ({ target, target_id: targetId, config });

  const preview = useMutation({
    mutationFn: () => api.post<Preview>('/admin/composer/preview', body()),
    onSuccess: () => setConfirming(true),
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not read that'), tone: 'bad' }),
  });

  const apply = useMutation({
    mutationFn: () =>
      api.post<Applied>('/admin/composer/apply', {
        ...body(),
        // What the operator was just shown. If the engine has gained or lost
        // a bot while they were reading, the server refuses rather than
        // quietly doing something larger than they agreed to.
        ...(target === 'engine'
          ? { expect_bots: preview.data?.affected_bots ?? -1 }
          : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: (result) => {
      setConfirming(false);
      setUndo(result);
      // So the list below shows it without a reload.
      void qc.invalidateQueries({ queryKey: ['composer-history'] });
      setToast({
        message: `Applied to ${result.name}.`,
        tone: 'good',
      });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not apply it'), tone: 'bad' }),
  });

  const revert = useMutation({
    mutationFn: (auditId: number) =>
      api.post<{ reverted: boolean; name: string }>('/admin/composer/revert', {
        audit_id: auditId,
      }),
    onSuccess: (result) => {
      setUndo(null);
      setToast({ message: `Put ${result.name} back as it was.`, tone: 'good' });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not undo it'), tone: 'bad' }),
  });

  if (!Object.keys(config).length) return null;

  const chosen =
    target === 'engine'
      ? engines.find((e) => e.id === targetId)
      : bots.find((b) => b.id === targetId);

  return (
    <div className="mt-4 rounded-xl border border-line bg-sunken p-4">
      <p className="text-xs font-medium text-ink-700 dark:text-ink-200">
        Apply this
      </p>
      <p className="mt-0.5 text-2xs text-ink-500">
        Nothing above has changed anything yet. This writes the company
        details, the prompt, the qualification and the mode — and nothing else,
        ever.
      </p>

      <div className="mt-3 grid gap-2 sm:grid-cols-[10rem,1fr]">
        <Select
          value={target}
          onChange={(e) => {
            setTarget(e.target.value as 'bot' | 'engine');
            setTargetId('');
          }}
          className="text-xs"
        >
          <option value="bot">One bot</option>
          <option value="engine">A bot engine</option>
        </Select>

        <Select
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          className="text-xs"
        >
          <option value="">
            {target === 'engine' ? 'Choose an engine…' : 'Choose a bot…'}
          </option>
          {target === 'engine'
            ? engines.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} — {e.bots} bot{e.bots === 1 ? '' : 's'}
                </option>
              ))
            : bots.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} — {b.client_name}
                </option>
              ))}
        </Select>
      </div>

      <Input
        value={reason}
        placeholder="Why (optional, kept with the change)"
        className="mt-2 text-xs"
        onChange={(e) => setReason(e.target.value)}
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={!targetId}
          loading={preview.isPending}
          onClick={() => preview.mutate()}
        >
          Review and apply
        </Button>
        {target === 'engine' && chosen ? (
          <Badge tone="amber">
            changes {(chosen as { bots: number }).bots} bot
            {(chosen as { bots: number }).bots === 1 ? '' : 's'}
          </Badge>
        ) : null}
      </div>

      {/* The undo, offered where the person is looking rather than filed in an
          audit log they would have to go and find. */}
      {undo?.audit_id ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-900 dark:bg-emerald-950/40">
          <span className="text-xs text-emerald-900 dark:text-emerald-200">
            Applied to <strong>{undo.name}</strong> ({undo.keys.join(', ')}).
          </span>
          <Button
            size="sm"
            variant="secondary"
            loading={revert.isPending}
            onClick={() => revert.mutate(undo.audit_id as number)}
          >
            Undo
          </Button>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirming && !!preview.data}
        title={
          preview.data?.target === 'engine'
            ? `Change ${preview.data?.affected_bots} bot${preview.data?.affected_bots === 1 ? '' : 's'}?`
            : `Change ${preview.data?.name}?`
        }
        confirmLabel="Apply"
        busy={apply.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => apply.mutate()}
        body={
          <div className="space-y-3 text-sm">
            {preview.data?.warning ? (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                {preview.data.warning}
              </p>
            ) : null}

            {/* Field by field, not a JSON diff. A raw before/after of two
                nested objects is not a review, it is homework — and somebody
                asked to confirm something they cannot read will confirm it. */}
            {preview.data?.changes.length ? (
              <ul className="max-h-72 space-y-2 overflow-auto">
                {preview.data.changes.map((change) => (
                  <li key={change.field} className="text-xs">
                    <div className="font-mono text-ink-500">{change.field}</div>
                    <div className="mt-0.5 text-red-700 line-through dark:text-red-400">
                      {value(change.from)}
                    </div>
                    <div className="text-emerald-700 dark:text-emerald-400">
                      {value(change.to)}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-ink-500">
                Nothing would change — this configuration already matches.
              </p>
            )}

            {preview.data?.dropped.length ? (
              <p className="text-xs text-ink-500">
                Not applied, because the Composer may never set them:{' '}
                {preview.data.dropped.join(', ')}.
              </p>
            ) : null}

            <p className="text-xs text-ink-500">
              This can be undone straight afterwards.
            </p>
          </div>
        }
      />

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}


interface Effect {
  verdict: string;
  enough_to_judge: boolean;
  window_hours?: number;
  caveat?: string;
  before?: { connected: number; transfer_rate: number | null; qualified_rate: number | null };
  after?: { connected: number; transfer_rate: number | null; qualified_rate: number | null };
}

function Rate({ label, before, after }: { label: string; before: number | null | undefined; after: number | null | undefined }) {
  if (before === null || before === undefined || after === null || after === undefined) {
    return null;
  }
  const delta = Math.round((after - before) * 10) / 10;
  return (
    <div className="text-xs">
      <span className="text-ink-500">{label} </span>
      <span className="text-ink-700 dark:text-ink-200">{before}%</span>
      <span className="text-ink-400"> → </span>
      <span className="font-medium text-ink-900 dark:text-ink-50">{after}%</span>
      {Math.abs(delta) >= 1 ? (
        <span
          className={
            delta > 0
              ? 'ml-1 text-emerald-600 dark:text-emerald-400'
              : 'ml-1 text-red-600 dark:text-red-400'
          }
        >
          {delta > 0 ? '+' : ''}
          {delta}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Whether a change helped.
 *
 * Loaded on demand rather than with the list: it is a pair of aggregates over
 * the calls either side of one change, and asking for twenty-five of them to
 * render a page nobody has clicked on yet is a query per row for nothing.
 */
function DidItWork({ auditId }: { auditId: number }) {
  const [asked, setAsked] = useState(false);
  const effect = useQuery({
    queryKey: ['composer-effect', auditId],
    queryFn: () => api.get<Effect>('/admin/composer/effect', { audit_id: auditId }),
    enabled: asked,
  });

  if (!asked) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setAsked(true)}>
        Did it work?
      </Button>
    );
  }
  if (effect.isPending) return <span className="text-xs text-ink-400">reading…</span>;
  if (effect.error) {
    return <span className="text-xs text-red-500">could not measure it</span>;
  }

  const data = effect.data;
  return (
    <div className="max-w-sm text-right">
      <p
        className={
          data.enough_to_judge
            ? 'text-xs font-medium text-ink-900 dark:text-ink-50'
            : 'text-xs text-ink-500'
        }
      >
        {data.verdict}
      </p>
      {data.enough_to_judge ? (
        <div className="mt-1 space-y-0.5">
          <Rate
            label="transferred"
            before={data.before?.transfer_rate}
            after={data.after?.transfer_rate}
          />
          <Rate
            label="qualified"
            before={data.before?.qualified_rate}
            after={data.after?.qualified_rate}
          />
          {/* The caveat is not small print. Everything else moved too. */}
          <p className="mt-1 text-2xs leading-snug text-ink-400">{data.caveat}</p>
        </div>
      ) : null}
    </div>
  );
}

interface HistoryRow {
  audit_id: number;
  target: string;
  name: string;
  actor: string | null;
  at: string;
  keys: string[];
  affected_bots: number | null;
  reason: string | null;
  reverted: boolean;
  can_revert: boolean;
}

/**
 * What the Composer has changed, with an undo on each one.
 *
 * The undo used to exist only in the panel that had just applied something.
 * Navigate away — or come back tomorrow having heard the bot got worse — and
 * the record was in the audit log with no button anywhere near it.
 */
export function ComposerHistory() {
  const qc = useQueryClient();
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  const history = useQuery({
    queryKey: ['composer-history'],
    queryFn: () => api.get<{ data: HistoryRow[] }>('/admin/composer/history'),
  });

  const revert = useMutation({
    mutationFn: (auditId: number) =>
      api.post<{ name: string }>('/admin/composer/revert', { audit_id: auditId }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['composer-history'] });
      setToast({ message: `Put ${result.name} back as it was.`, tone: 'good' });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not undo it'), tone: 'bad' }),
  });

  const rows = history.data?.data ?? [];
  if (!rows.length) return null;

  return (
    <div className="mt-6 rounded-xl border border-line bg-surface p-4">
      <p className="text-sm font-medium text-ink-900 dark:text-ink-50">
        What the Composer has changed
      </p>
      <p className="mt-0.5 text-xs text-ink-500">
        Every apply, newest first. Each one can be put back.
      </p>

      <ul className="mt-3 divide-y divide-line">
        {rows.map((row) => (
          <li
            key={row.audit_id}
            className="flex flex-wrap items-center justify-between gap-2 py-2"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-ink-900 dark:text-ink-50">
                  {row.name}
                </span>
                {row.target === 'engine' ? (
                  <Badge tone="amber">
                    engine · {row.affected_bots ?? '?'} bot
                    {row.affected_bots === 1 ? '' : 's'}
                  </Badge>
                ) : (
                  <Badge tone="gray">bot</Badge>
                )}
                {row.reverted ? <Badge tone="blue">put back</Badge> : null}
              </div>
              <p className="mt-0.5 text-2xs text-ink-500">
                {row.keys.join(', ') || 'nothing'} · {row.actor ?? 'unknown'} ·{' '}
                {new Date(row.at).toLocaleString()}
              </p>
              {row.reason ? (
                <p className="mt-0.5 text-2xs text-ink-400">{row.reason}</p>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <DidItWork auditId={row.audit_id} />
              {row.can_revert ? (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={revert.isPending && revert.variables === row.audit_id}
                  onClick={() => revert.mutate(row.audit_id)}
                >
                  Undo
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
